<template>
  <div class="component-wrapper inner d-flex flex-column align-center">
    <v-btn icon color="primary" @click="onClose" class="close-button">
      <v-icon>mdi-close</v-icon>
    </v-btn>

    <div class="body-2 text-center mb-2" v-if="role == 'superuser'">
      ({{ activeMuseum.defaultTitle }})
    </div>

    <div class="text-h6 primary--text text-center mb-1">{{ $t('step3') }}</div>

    <div class="text-h6 text-center title-underline mb-12">
      {{ $t('createScenario') }}
    </div>

    <div class="inner-wrapper d-flex flex-column">
      <Step1Info class="mb-8" />

      <div class="subtitle-1 font-weight-medium mb-4">{{ $t('sections') }}</div>

      <draggable v-model="sections" :animation="200">
        <transition-group>
          <div
            class="pa-2 primary mb-2 rounded white--text d-flex align-center"
            :class="{
              primary: openSection && openSection.id == section.id,
              'warning lighten-1': openSection && openSection.id == section.id,
            }"
            v-for="section in sections"
            :key="section.id"
          >
            <div class="body-1">{{ section.order }}. {{ section.defaultTitle }}</div>

            <v-btn
              v-if="openSection && openSection.id == section.id"
              small
              class="ml-auto"
              :lang="$i18n.locale"
              @click="setOpenSection(null)"
            >
              <v-icon left>mdi-close</v-icon>
              {{ $t('close') }}
            </v-btn>

            <v-btn
              v-else
              small
              class="ml-auto"
              :lang="$i18n.locale"
              @click="onOpenSection(section)"
            >
              <v-icon left>mdi-arrow-expand</v-icon>
              {{ $t('open') }}
            </v-btn>
          </div>
        </transition-group>
      </draggable>

      <template v-if="openSection">
        <div class="assets-wrapper mt-10">
          <draggable v-model="openSectionAssets" :animation="200">
            <div
              v-for="asset in openSectionAssets"
              :key="asset.id"
              class="item"
            >
              <div class="item-content-wrapper rounded">
                <div class="item-actions-wrapper d-flex align-center">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on">
                        <v-checkbox
                          class="ma-0 pa-0 reorder-checkbox"
                          hide-details
                          :ripple="false"
                          v-model="asset.presentationInclude"
                          @change="onUpdateSectionAssetsOrder"
                        >
                        </v-checkbox>
                      </div>
                    </template>

                    <div class="body-2">{{ $t('includeInPresentation') }}</div>
                  </v-tooltip>

                  <v-tooltip top v-if="asset.type == 'LINK'">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="has-sound-icon rounded-bl primary ml-auto"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{
                          asset.hasSound
                            ? "mdi-music-note"
                            : "mdi-music-note-off"
                        }}
                      </v-icon>
                    </template>

                    <span>{{
                      asset.hasSound
                        ? $t('videoHasSound')
                        : $t('videoIsMuted')
                    }}</span>
                  </v-tooltip>
                </div>

                <img
                  :src="asset.resolvedThumbUrl"
                  v-if="asset.type == 'IMAGE'"
                  class="item-content"
                />

                <div
                  class="item-content video"
                  v-if="asset.type == 'LINK'"
                  @click="openVideo(asset.storedPath)"
                >
                  <img :src="asset.storedThumbPath" />
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </template>

      <v-btn
        color="primary"
        class="align-self-center mt-10"
        :loading="loader"
        :disabled="!hasChanges"
        :lang="$i18n.locale"
        @click="onSave"
      >
        <v-icon left>mdi-content-save</v-icon>
        {{ $t('save') }}
      </v-btn>

      <v-divider class="my-10"></v-divider>

      <div class="d-flex">
        <v-btn
          color="primary"
          small
          :lang="$i18n.locale"
          @click="onPrev"
        >
          <v-icon left>mdi-chevron-left</v-icon>
          {{ $t('previous') }}
        </v-btn>

        <v-btn
          class="ml-auto"
          color="primary"
          small
          :disabled="hasChanges"
          :loading="submitLoader"
          :lang="$i18n.locale"
          @click="onFinish"
        >
          {{
            isEdit && !currentProgram.isDraft
              ? $t('completion')
              : $t('submitProgram')
          }}
          <v-icon right>mdi-check</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapState, mapActions, mapMutations } from "vuex";
import Step1Info from "@/views/program/step1/Step1Info";
import axios from 'axios';

export default {
  components: {
    draggable,
    Step1Info,
  },
  data() {
    return {
      baseUrl: process.env.VUE_APP_BASE_URL,
      loader: false,
      submitLoader: false,
      initialData: null,
    };
  },
  computed: {
    ...mapState({
      openSection: (state) => state.admin.openSection,
      currentProgram: (state) => state.admin.currentProgram,
      role: (state) => state.auth.role,
      activeMuseum: (state) => state.superuser.activeMuseum,
    }),
    isEdit() {
      return this.$route.path.includes("edit");
    },
    sections: {
      get() {
        return this.$store.state.admin.currentProgram.sections;
      },
      set(value) {
        this.updateSectionsOrder(value);
      },
    },
    openSectionAssets: {
      get() {
        return this.$store.state.admin.openSection.assets.filter(
          (asset) => asset.type != "SOUND"
        );
      },
      set(value) {
        this.updateSectionAssetsOrder(value);
      },
    },
    hasChanges() {
      return (
        JSON.stringify(this.initialData) !==
        JSON.stringify({ ...this.currentProgram })
      );
    },
  },
  created() {
    this.initialData = JSON.parse(JSON.stringify(this.currentProgram));
  },
  methods: {
    ...mapMutations([
      "updateSectionsOrder",
      "setOpenSection",
      "updateSectionAssetsOrder"
    ]),
    ...mapActions([
      "submitNewProgram",
      "getProgram",
      "getPrograms"
    ]),
    openVideo(path) {
      window.open(path);
    },
    async onOpenSection(section) {
      this.loader = true;
      for (let k = 0; k < section.assets.length; k++) {
        let asset = section.assets[k];
        if (asset.type == 'IMAGE') {
          try {
            const response = await axios.get(
              `${process.env.VUE_APP_BASE_URL}/assets/serve/${asset.storedThumbPath}`,
              {
                responseType: 'blob'
              }
            );
            asset.resolvedThumbUrl = URL.createObjectURL(response.data);
          } catch (err) {
            console.error(`Failed to upload image #${k + 1}`, err);
          }
        }
      }
      this.setOpenSection(section);
      this.loader = false;
    },
    async onSave() {
      this.loader = true; 
      let initialSections = this.initialData.sections;
      let currentSections = this.currentProgram.sections;

      // if section ordering has changed update sections
      if ( JSON.stringify(initialSections) !== JSON.stringify({ ...currentSections }) ) {
        for (let k = 0; k < this.currentProgram.sections.length; k++) {
          let section = this.currentProgram.sections[k];
          let sectionData = {
            ordering: k + 1
          }

          try {
            await axios.put(`${process.env.VUE_APP_BASE_URL}/sections/${section.id}`, sectionData);
          } catch (err) {
            console.error(`Failed to update section #${k + 1}`, err);
          }

          let assets = section.assets.sort((a, b) => b.presentationInclude - a.presentationInclude);
          for (let j = 0; j < assets.length; j++) {
            let asset = assets[j];
            let ordering = -1;
            if (asset.presentationInclude) {
              ordering = j + 1;
            }

            let assetFormData = new FormData();
            assetFormData.set("sectionId", section.id);
            assetFormData.set("assetId", asset.id);
            assetFormData.set("type", asset.type);
            assetFormData.set("ordering", ordering);
            assetFormData.set("hasSound", asset.hasSound);
            assetFormData.set("presentationInclude", asset.presentationInclude);

            try {
              await axios.put(`${process.env.VUE_APP_BASE_URL}/assets/${asset.id}`, assetFormData);
            } catch (err) {
              console.error(`Failed to update asset #${k + 1}`, err);
            }
          }
        }
      }
      
      await this.getProgram(this.currentProgram.id);
      await this.getPrograms();

      this.initialData = JSON.parse(
        JSON.stringify(this.currentProgram)
      );

      this.loader = false;
    },
    onClose() {
      if (this.role == "admin") {
        this.$router.push({ path: "/" });
      } else {
        this.$router.push({
          path: `/superuser/museum/${this.activeMuseum.id}`,
        });
      }
    },
    onPrev() {
      let path;

      if (this.isEdit) {
        if (this.role == "admin") {
          path = `/program/edit/${this.currentProgram.uniqueCode}/step2`;
        } else {
          path = `/superuser/museum/${this.activeMuseum.id}/program/edit/${this.currentProgram.uniqueCode}/step2`;
        }
      } else {
        if (this.role == "admin") {
          path = `/program/new/step2`;
        } else {
          path = `/superuser/museum/${this.activeMuseum.id}/program/new/step2`;
        }
      }

      this.$router.push({
        path,
      });
    },
    async onFinish() {
      if (this.isEdit && !this.currentProgram.isDraft) {
        this.onClose();
        return;
      }

      this.submitLoader = true;

      try {
        await this.submitNewProgram();
        this.onClose();
      } catch (e) {}

      this.submitLoader = false;
    },
    onUpdateSectionAssetsOrder() {
      this.updateSectionAssetsOrder(this.openSectionAssets);
    }
  },
  destroyed() {
    this.setOpenSection(null);
  },
};
</script>

<style lang="scss" scoped>
.assets-wrapper > div {
  display: flex;
  flex-wrap: wrap;
}

.item {
  width: 25%;
  padding: 4px;
}

.item-content-wrapper {
  padding-bottom: 100%;
  overflow: hidden;
  position: relative;
  border: 1px solid #e0e0e0;
}

.item-actions-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
  background-color: rgba(#fff, 0.5);
  height: 24px;
  padding-left: 4px;

  .has-sound-icon {
    color: white;
    padding: 1px;
  }
}

.item-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: grabbing;

  &.video {
    img {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  }
}
</style>